import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * `<plw-recently-viewed>` — "You recently looked at" carousel in the cart drawer
 * (CQ-1959). Reads the recently-viewed product ids from localStorage, fetches the
 * `plw-recently-viewed` section rendered for an `id:.. OR ..` product search, and
 * injects the resulting scroller of universal product cards. Products already in
 * the cart are excluded (their ids arrive via `data-exclude`). Hides itself when
 * there is nothing to show. Pure enhancement — empty/inert with JS off.
 */
class PlwRecentlyViewed extends HTMLElement {
  #connected = false;
  #rendering = false;
  #observer = null;
  /** The exclusion-filtered id list the current children were rendered from. */
  #renderedKey = null;

  connectedCallback() {
    this.#connected = true;
    this.#render();

    // Cart updates morph the whole header section (component-cart-items.js).
    // The host element carries data-skip-subtree-update, so the morph preserves
    // the injected carousel children but still re-syncs attributes — re-adding
    // the server-rendered `hidden` (and refreshing data-exclude). Un-hide
    // immediately so the band never flashes, and refetch ONLY when the id list
    // actually changed (e.g. a carted product must drop out); a plain quantity
    // tick costs no network and no repaint. The empty+hidden branch covers a
    // morph that DID wipe the children (attribute missing on either side).
    this.#observer = new MutationObserver(() => {
      if (this.#rendering) return;
      if (this.hidden && this.childElementCount === 0) {
        this.#render();
      } else if (this.hidden && this.childElementCount > 0) {
        this.hidden = false;
        if (this.#idsKey() !== this.#renderedKey) this.#render();
      }
    });
    this.#observer.observe(this, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-exclude'],
    });
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  async #render() {
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      await this.#renderInner();
    } finally {
      this.#rendering = false;
    }
  }

  /** Recently-viewed ids minus the carted ones — the carousel's identity. */
  #idsKey() {
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    return RecentlyViewed.getProducts()
      .filter((id) => !exclude.has(String(id)))
      .join(',');
  }

  async #renderInner() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.hidden = true;
      return;
    }

    const key = this.#idsKey();
    const ids = key.split(',').filter(Boolean);

    if (ids.length === 0) {
      this.#renderedKey = key;
      this.hidden = true;
      this.replaceChildren();
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (!this.#connected) return; // drawer/section was torn down mid-fetch

      const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recently-root]');

      if (!root || !root.querySelector('.plw-recently__item')) {
        this.#renderedKey = key;
        this.hidden = true;
        this.replaceChildren();
        return;
      }

      const fragment = document.createDocumentFragment();
      for (const node of [...root.childNodes]) {
        fragment.appendChild(document.importNode(node, true));
      }
      this.replaceChildren(fragment);
      this.hidden = false;
      this.#renderedKey = key;
    } catch (error) {
      console.error('[plw-recently-viewed] failed to load', error);
      this.#renderedKey = null;
      this.hidden = true;
      this.replaceChildren();
    }
  }
}

if (!customElements.get('plw-recently-viewed')) {
  customElements.define('plw-recently-viewed', PlwRecentlyViewed);
}
